// Conversation Timer Debug Utility

export const conversationDebug = {
  // Check if a conversation is active (within 24 hours)
  isConversationActive: (conversation) => {
    if (!conversation || !conversation.expiration_timestamp) {
      return false;
    }
    
    const expirationTime = new Date(conversation.expiration_timestamp * 1000);
    const now = new Date();
    const timeLeft = expirationTime - now;
    
    return timeLeft > 0;
  },

  // Get detailed info about a conversation
  getConversationInfo: (conversation) => {
    if (!conversation || !conversation.expiration_timestamp) {
      return { error: 'Invalid conversation data' };
    }
    
    const expirationTime = new Date(conversation.expiration_timestamp * 1000);
    const now = new Date();
    const timeLeft = expirationTime - now;
    const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    // Format times in a more readable way
    const formatDateTime = (date) => {
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    };
    
    return {
      id: conversation.id,
      type: conversation.origin?.type || 'unknown',
      expirationTimestamp: conversation.expiration_timestamp,
      expirationTime: formatDateTime(expirationTime),
      currentTime: formatDateTime(now),
      timeLeftMs: timeLeft,
      timeLeftHours: hoursLeft,
      timeLeftMinutes: minutesLeft,
      timeLeftFormatted: timeLeft > 0 ? `${hoursLeft}h ${minutesLeft}m` : 'EXPIRED',
      isActive: timeLeft > 0,
      isExpired: timeLeft <= 0,
      // Additional helpful info
      expiresInHumanTime: timeLeft > 0 
        ? `Expires in ${hoursLeft}h ${minutesLeft}m` 
        : `Expired ${Math.abs(hoursLeft)}h ${Math.abs(minutesLeft)}m ago`
    };
  },

  // Check if a message is a template message
  isTemplateMessage: (message) => {
    if (!message) return false;
    
    // Check if it's a template message (has text.body.name)
    if (message.text && 
        message.text.body && 
        typeof message.text.body === 'object' && 
        message.text.body.name) {
        return true;
    }
    
    return false;
  },

  // Check if last message allows 24-hour window
  canSendBasedOnLastMessage: (contact) => {
    if (!contact || !contact.lastMessage) {
      return { canSend: false, reason: 'No last message found' };
    }
    
    let lastMessage = contact.lastMessage;
    let lastMessageTimestamp = contact.lastMessageTimestamp;
    
    // If lastMessage is an object with entry structure, extract the actual message
    if (lastMessage.entry && 
        lastMessage.entry[0] && 
        lastMessage.entry[0].changes && 
        lastMessage.entry[0].changes[0] && 
        lastMessage.entry[0].changes[0].value && 
        lastMessage.entry[0].changes[0].value.messages && 
        lastMessage.entry[0].changes[0].value.messages[0]) {
        
        const messageData = lastMessage.entry[0].changes[0].value.messages[0];
        lastMessage = messageData;
        lastMessageTimestamp = messageData.timestamp;
    }
    
    // Check if the last message is a template message or from the contact
    const isTemplate = conversationDebug.isTemplateMessage(lastMessage);
    const isFromContact = lastMessage.from !== process.env.REACT_APP_PHONE_NUMBER;
    
    // If it's a template message or from contact, check 24-hour window
    if (isTemplate || isFromContact) {
        const messageTime = new Date(lastMessageTimestamp * 1000);
        const now = new Date();
        const timeElapsed = now - messageTime;
        const twentyFourHours = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        const hoursElapsed = Math.floor(timeElapsed / (1000 * 60 * 60));
        const minutesElapsed = Math.floor((timeElapsed % (1000 * 60 * 60)) / (1000 * 60));
        
        return {
          canSend: timeElapsed < twentyFourHours,
          isTemplate,
          isFromContact,
          messageTime: messageTime.toLocaleString(),
          currentTime: now.toLocaleString(),
          timeElapsedHours: hoursElapsed,
          timeElapsedMinutes: minutesElapsed,
          timeElapsedFormatted: `${hoursElapsed}h ${minutesElapsed}m`,
          withinWindow: timeElapsed < twentyFourHours,
          reason: timeElapsed < twentyFourHours 
            ? `Within 24h window (${hoursElapsed}h ${minutesElapsed}m elapsed)` 
            : `Outside 24h window (${hoursElapsed}h ${minutesElapsed}m elapsed)`
        };
    }
    
    return { canSend: false, reason: 'Last message not template or from contact' };
  },

  // Analyze all conversations for a contact
  analyzeContact: (contact) => {
    if (!contact) {
      return { error: 'No contact provided' };
    }

    const conversations = contact.conversation || [];
    const analysis = {
      contactId: contact.Celular || contact.wa_id || 'unknown',
      contactName: contact.NombreCompleto || 'Unknown',
      totalConversations: conversations.length,
      activeConversations: 0,
      expiredConversations: 0,
      conversations: []
    };

    conversations.forEach((conv, index) => {
      const info = conversationDebug.getConversationInfo(conv);
      analysis.conversations.push({
        index,
        ...info
      });

      if (info.isActive) {
        analysis.activeConversations++;
      } else {
        analysis.expiredConversations++;
      }
    });

    // Check conversation timers
    analysis.hasActiveTimer = analysis.activeConversations > 0;
    
    // Check last message 24-hour window
    analysis.lastMessageCheck = conversationDebug.canSendBasedOnLastMessage(contact);
    
    // Final decision: can send if either condition is true
    analysis.canSendMessages = analysis.hasActiveTimer || analysis.lastMessageCheck.canSend;

    return analysis;
  },

  // Test with sample data
  testWithSampleData: () => {
    const now = Date.now();
    const oneHourFromNow = Math.floor((now + (60 * 60 * 1000)) / 1000);
    const oneHourAgo = Math.floor((now - (60 * 60 * 1000)) / 1000);
    const twentyThreeHoursFromNow = Math.floor((now + (23 * 60 * 60 * 1000)) / 1000);

    const testContact = {
      Celular: "1234567890",
      NombreCompleto: "Test Contact",
      conversation: [
        {
          id: "active1",
          expiration_timestamp: oneHourFromNow.toString(),
          origin: { type: "service" }
        },
        {
          id: "expired1", 
          expiration_timestamp: oneHourAgo.toString(),
          origin: { type: "service" }
        },
        {
          id: "active2",
          expiration_timestamp: twentyThreeHoursFromNow.toString(),
          origin: { type: "user_initiated" }
        }
      ]
    };

    return conversationDebug.analyzeContact(testContact);
  }
};

// Make it available globally for debugging in browser console
if (typeof window !== 'undefined') {
  window.conversationDebug = conversationDebug;
}
